if (!customElements.get('bundle-builder')) {
  customElements.define(
    'bundle-builder',
    class BundleBuilder extends HTMLElement {
      connectedCallback() {
        if (this.initialized) return;
        this.initialized = true;

        this.config = this.readConfig();
        this.items = new Map();
        this.purchaseType =
          this.config.showPurchaseToggle === false ? 'onetime' : this.config.defaultPurchaseType || 'subscribe';
        this.frequency = this.config.frequencies[0] ? this.config.frequencies[0].value : '';
        this.activeTab = 0;
        this.pages = {};
        this.lastFocus = null;

        this.cacheDom();
        this.mergeFrequenciesFromProducts();
        this.bindEvents();
        this.syncPurchaseType();
        this.renderTabs();
        this.updateAll();
        this.syncDesktopAtcMinWidth();
        this.setupDesktopStickyHeader();
        this.setupMobileStickyTabs();
      }

      readConfig() {
        const script = this.querySelector('[data-bundle-config]');
        const parsed = script ? JSON.parse(script.textContent) : {};
        return Object.assign(
          {
            moneyFormat: '${{amount}}',
            frequencies: [],
            tiers: [],
            subscriptionDiscount: 10,
            minItemsToCheckout: 3,
            maxProductsInBundle: 10,
            productsPerPage: 8,
            defaultPurchaseType: 'subscribe',
            messages: {},
            properties: {},
          },
          parsed
        );
      }

      cacheDom() {
        this.tabButtons = Array.from(this.querySelectorAll('[data-bundle-tab]'));
        this.panels = Array.from(this.querySelectorAll('[data-bundle-panel]'));
        this.cards = Array.from(this.querySelectorAll('[data-bundle-card]'));
        this.purchaseButtons = Array.from(this.querySelectorAll('[data-bundle-purchase]'));
        this.frequencySelects = Array.from(this.querySelectorAll('[data-bundle-frequency]'));
        this.frequencyWraps = Array.from(this.querySelectorAll('[data-bundle-frequency-wrap]'));
        this.deliveryTexts = Array.from(this.querySelectorAll('[data-bundle-delivery]'));
        this.atcButtons = Array.from(this.querySelectorAll('[data-bundle-atc]'));
        this.progressFills = Array.from(this.querySelectorAll('[data-bundle-progress-fill]'));
        this.progressNodes = Array.from(this.querySelectorAll('[data-bundle-node]'));
        this.countEls = Array.from(this.querySelectorAll('[data-bundle-count]'));
        this.headingEls = Array.from(this.querySelectorAll('[data-bundle-heading]'));
        this.paginationEls = Array.from(this.querySelectorAll('[data-bundle-pagination]'));
        this.errorEls = Array.from(this.querySelectorAll('[data-bundle-error]'));
        this.drawer = this.querySelector('[data-bundle-drawer]');
        this.drawerOverlay = this.querySelector('[data-bundle-drawer-overlay]');
        this.drawerTitle = this.querySelector('[data-bundle-drawer-title]');
        this.summaryRoot = this.querySelector('[data-bundle-summary]');
        this.stickyCompare = this.querySelector('[data-bundle-sticky-compare]');
        this.stickyTotal = this.querySelector('[data-bundle-sticky-total]');
        this.viewButtons = Array.from(this.querySelectorAll('[data-bundle-view]'));
        this.closeButtons = Array.from(this.querySelectorAll('[data-bundle-close]'));
        this.headerEl = this.querySelector('.bundle-builder__header');
        this.headerSentinel = this.querySelector('[data-bundle-header-sentinel]');
        this.headerSpacer = this.querySelector('[data-bundle-header-spacer]');
        this.tabsEl = this.querySelector('[data-bundle-tabs]');
        this.tabsSentinel = this.querySelector('[data-bundle-tabs-sentinel]');
        this.tabsSpacer = this.querySelector('[data-bundle-tabs-spacer]');
        this.drawerItems = this.querySelector('[data-bundle-drawer-items]');
      }

      bindEvents() {
        this.tabButtons.forEach((button) => {
          button.addEventListener('click', () => {
            const nextTab = Number(button.dataset.bundleTab) || 0;
            const tabChanged = nextTab !== this.activeTab;
            this.activeTab = nextTab;
            this.pages[this.activeTab] = this.pages[this.activeTab] || 1;
            this.renderTabs();
            if (tabChanged) this.scrollToProductGridIfSticky();
          });
        });

        this.purchaseButtons.forEach((button) => {
          button.addEventListener('click', () => {
            this.purchaseType = button.dataset.bundlePurchase;
            this.syncPurchaseType();
            this.updateAll();
          });
        });

        this.frequencySelects.forEach((select) => {
          select.addEventListener('change', () => {
            this.frequency = select.value;
            this.frequencySelects.forEach((other) => {
              other.value = this.frequency;
            });
            this.updateAll();
          });
        });

        this.cards.forEach((card) => {
          const product = this.getCardProduct(card);
          if (!product) return;

          const variantSelect = card.querySelector('[data-bundle-variant]');
          if (variantSelect) {
            variantSelect.addEventListener('change', () => {
              this.onVariantChange(card, product, variantSelect.value);
            });
          }

          const addButton = card.querySelector('[data-bundle-add]');
          if (addButton) {
            addButton.addEventListener('click', () => this.addProduct(card, product));
          }

          const minusButton = card.querySelector('[data-bundle-qty-minus]');
          const plusButton = card.querySelector('[data-bundle-qty-plus]');
          if (minusButton) minusButton.addEventListener('click', () => this.changeQty(product.id, -1));
          if (plusButton) plusButton.addEventListener('click', () => this.changeQty(product.id, 1));
        });

        this.atcButtons.forEach((button) => {
          button.addEventListener('click', () => this.addBundleToCart(button));
        });

        this.viewButtons.forEach((button) => {
          button.addEventListener('click', () => this.openDrawer(button));
        });

        this.closeButtons.forEach((button) => {
          button.addEventListener('click', () => this.closeDrawer());
        });

        if (this.drawerOverlay) {
          this.drawerOverlay.addEventListener('click', () => this.closeDrawer());
        }

        if (this.drawerItems) {
          this.drawerItems.addEventListener('click', (event) => {
            const button = event.target.closest('[data-bundle-remove]');
            if (!button) return;
            this.removeProduct(button.dataset.bundleRemove);
          });
        }

        this.addEventListener('keydown', (event) => {
          if (event.key === 'Escape' && this.classList.contains('is-drawer-open')) {
            this.closeDrawer();
          }
        });

        this.bindAnchorScroll();
      }

      bindAnchorScroll() {
        const id = this.id;
        if (!id || this.anchorScrollBound) return;
        this.anchorScrollBound = true;

        document.addEventListener('click', (event) => {
          const link = event.target.closest('a[href]');
          if (!link || !this.anchorLinkMatches(link, id)) return;

          event.preventDefault();
          if (window.location.hash !== `#${id}`) {
            history.pushState(null, '', `#${id}`);
          }
          this.scrollToSection();
        });
      }

      anchorLinkMatches(link, id) {
        const href = link.getAttribute('href');
        if (href === `#${id}`) return true;

        try {
          const url = new URL(link.href, window.location.href);
          return url.pathname === window.location.pathname && url.hash === `#${id}`;
        } catch (error) {
          return false;
        }
      }

      scrollToSection() {
        const isDesktop = window.matchMedia('(min-width: 750px)').matches;
        let top = this.getBoundingClientRect().top + window.scrollY;

        if (isDesktop && this.headerEl) {
          const headerDocTop = this.headerEl.classList.contains('is-stuck')
            ? this.headerAnchorY
            : this.headerEl.getBoundingClientRect().top + window.scrollY;

          if (headerDocTop != null) {
            top = headerDocTop - 100;
          }
        }

        window.scrollTo({
          top: Math.max(0, Math.round(top)),
          behavior: 'smooth',
        });
      }

      frequencyIntervalKey(text) {
        const source = String(text || '').toLowerCase().replace(/\s+/g, ' ').trim();
        const match = source.match(/(\d+)\s*(day|days|week|weeks|month|months)/);
        if (!match) return '';
        return `${Number(match[1])}-${match[2].replace(/s$/, '')}`;
      }

      isPromotionalPlan(name) {
        return /first order|off first/i.test(String(name || ''));
      }

      mergeFrequenciesFromProducts() {
        this.config.frequencies = (this.config.frequencies || [])
          .map((item) => ({
            value: item.value,
            label: item.label || item.value,
            delivery: item.delivery || this.config.messages.deliverySubscribe || '',
            interval: this.frequencyIntervalKey(item.value || item.label),
          }))
          .filter((item) => item.value);

        this.frequency = this.config.frequencies[0] ? this.config.frequencies[0].value : this.frequency;
        this.frequencySelects.forEach((select) => {
          select.innerHTML = this.config.frequencies
            .map(
              (item) =>
                `<option value="${this.escapeAttr(item.value)}">${this.escapeHtml(item.label || item.value)}</option>`
            )
            .join('');
          select.value = this.frequency;
        });
      }

      getCardProduct(card) {
        const script = card.querySelector('[data-bundle-product-json]');
        if (!script) return null;
        try {
          return JSON.parse(script.textContent);
        } catch (error) {
          return null;
        }
      }

      getVariant(product, variantId) {
        const id = Number(variantId);
        return product.variants.find((variant) => Number(variant.id) === id) || product.variants[0];
      }

      onVariantChange(card, product, variantId) {
        const variant = this.getVariant(product, variantId);
        if (!variant) return;

        const existing = this.items.get(String(product.id));
        if (existing) {
          existing.variantId = variant.id;
          this.items.set(String(product.id), existing);
        }

        this.updateCardVariant(card, product, variant);
        this.updateAll();
      }

      updateCardVariant(card, product, variant) {
        const image = card.querySelector('[data-bundle-image]');
        if (image && variant.featured_image) {
          image.src = variant.featured_image;
          image.removeAttribute('width');
          image.removeAttribute('height');
        }

        this.updateCardPrice(card, variant);

        const addButton = card.querySelector('[data-bundle-add]');
        if (addButton) addButton.disabled = !variant.available;
      }

      tabConfig(tabIndex) {
        return (this.config.tabs || [])[Number(tabIndex)] || {};
      }

      tabItemCount(tabIndex) {
        let count = 0;
        this.items.forEach((item) => {
          if (Number(item.tabIndex) === Number(tabIndex)) count += item.quantity;
        });
        return count;
      }

      setTabLimitMessage(tabIndex, message) {
        const panel = this.panels[Number(tabIndex)];
        if (!panel) return;
        const note = panel.querySelector('[data-bundle-tab-limit]');
        if (!note) return;
        note.textContent = message || '';
        note.hidden = !message;
      }

      quantityLimitMessage(key, extra) {
        return this.replaceTokens(this.config.messages[key] || '', extra);
      }

      canAddQuantity(card, product, extraQty) {
        const tabIndex = Number(card.dataset.tabIndex) || 0;
        const config = this.tabConfig(tabIndex);
        const maxInBundle = Number(this.config.maxProductsInBundle) || 0;

        if (maxInBundle > 0 && this.itemCount() + extraQty > maxInBundle) {
          return {
            ok: false,
            tabIndex,
            message: this.quantityLimitMessage('maxInBundle', {
              max: String(maxInBundle),
              tab: config.title || '',
            }),
          };
        }

        if (!config.customQuantity) return { ok: true, tabIndex };

        const item = this.items.get(String(product.id));
        const nextProductQty = (item ? item.quantity : 0) + extraQty;
        const maxPerProduct = Number(config.maxPerProduct);
        if (maxPerProduct > 0 && nextProductQty > maxPerProduct) {
          return {
            ok: false,
            tabIndex,
            message: this.quantityLimitMessage('maxPerProduct', {
              max: String(maxPerProduct),
              tab: config.title || '',
            }),
          };
        }

        const maxPerStep = Number(config.maxPerStep);
        const nextStepQty = this.tabItemCount(tabIndex) + extraQty;
        if (maxPerStep > 0 && nextStepQty > maxPerStep) {
          return {
            ok: false,
            tabIndex,
            message: this.quantityLimitMessage('maxPerStep', {
              max: String(maxPerStep),
              tab: config.title || '',
            }),
          };
        }

        return { ok: true, tabIndex };
      }

      unmetStepMinimum() {
        const tabs = this.config.tabs || [];
        return tabs.find((config, index) => {
          if (!config.customQuantity) return false;
          const min = Number(config.minPerStep) || 0;
          return min > 0 && this.tabItemCount(index) < min;
        });
      }

      addProduct(card, product) {
        const select = card.querySelector('[data-bundle-variant]');
        const variant = this.getVariant(product, select ? select.value : product.variants[0].id);
        if (!variant || !variant.available) return;

        const limit = this.canAddQuantity(card, product, 1);
        if (!limit.ok) {
          this.setTabLimitMessage(limit.tabIndex, limit.message);
          return;
        }

        this.items.set(String(product.id), {
          productId: product.id,
          variantId: variant.id,
          quantity: 1,
          title: product.title,
          price: variant.price,
          tabIndex: Number(card.dataset.tabIndex) || 0,
        });
        this.setTabLimitMessage(Number(card.dataset.tabIndex) || 0, '');
        this.updateAll();
      }

      changeQty(productId, delta) {
        const key = String(productId);
        const item = this.items.get(key);
        if (!item) return;

        const card =
          this.cards.find(
            (node) =>
              Number(node.dataset.productId) === Number(productId) &&
              Number(node.dataset.tabIndex) === Number(item.tabIndex)
          ) || this.cards.find((node) => Number(node.dataset.productId) === Number(productId));

        if (delta > 0 && card) {
          const limit = this.canAddQuantity(card, this.getCardProduct(card) || { id: productId }, delta);
          if (!limit.ok) {
            this.setTabLimitMessage(limit.tabIndex, limit.message);
            return;
          }
        }

        const next = item.quantity + delta;
        if (next <= 0) {
          this.items.delete(key);
        } else {
          item.quantity = next;
          this.items.set(key, item);
        }
        if (card) this.setTabLimitMessage(Number(card.dataset.tabIndex) || 0, '');
        this.updateAll();
      }

      removeProduct(productId) {
        const key = String(productId);
        const item = this.items.get(key);
        if (!item) return;
        this.items.delete(key);

        const card =
          this.cards.find(
            (node) =>
              Number(node.dataset.productId) === Number(productId) &&
              Number(node.dataset.tabIndex) === Number(item.tabIndex)
          ) || this.cards.find((node) => Number(node.dataset.productId) === Number(productId));
        if (card) this.setTabLimitMessage(Number(card.dataset.tabIndex) || 0, '');
        this.updateAll();
      }

      itemCount() {
        let count = 0;
        this.items.forEach((item) => {
          count += item.quantity;
        });
        return count;
      }

      subtotal() {
        let total = 0;
        this.items.forEach((item) => {
          const card = this.cards.find((node) => Number(node.dataset.productId) === Number(item.productId));
          const product = card ? this.getCardProduct(card) : null;
          const variant = product ? this.getVariant(product, item.variantId) : null;
          const price = variant ? variant.price : item.price || 0;
          total += price * item.quantity;
        });
        return total;
      }

      currentTier() {
        const count = this.itemCount();
        const tiers = (this.config.tiers || []).slice().sort((a, b) => a.items - b.items);
        let current = null;
        tiers.forEach((tier) => {
          if (count >= Number(tier.items)) current = tier;
        });
        return current;
      }

      nextTier() {
        const count = this.itemCount();
        const tiers = (this.config.tiers || []).slice().sort((a, b) => a.items - b.items);
        return tiers.find((tier) => count < Number(tier.items)) || null;
      }

      maxTierItems() {
        const tiers = this.config.tiers || [];
        return tiers.reduce((max, tier) => Math.max(max, Number(tier.items) || 0), 1);
      }

      bundleDiscountPercent() {
        if (this.purchaseType === 'subscribe') return 0;
        const tier = this.currentTier();
        return tier ? Number(tier.percent) || 0 : 0;
      }

      subscriptionDiscountPercent() {
        if (this.purchaseType !== 'subscribe') return 0;
        return Number(this.config.subscriptionDiscount) || 0;
      }

      discountedTotal() {
        const subtotal = this.subtotal();
        const percent = this.bundleDiscountPercent() || this.subscriptionDiscountPercent();
        return Math.round(subtotal * (1 - percent / 100));
      }

      firstThresholdItems() {
        const tiers = (this.config.tiers || []).slice().sort((a, b) => a.items - b.items);
        return Number(tiers[0] && tiers[0].items) || Number(this.config.minItemsToCheckout) || 3;
      }

      remainingToFinal() {
        return Math.max(this.maxTierItems() - this.itemCount(), 0);
      }

      replaceTokens(template, extra) {
        const count = this.itemCount();
        const remaining = this.remainingToFinal();
        const tokens = Object.assign(
          {
            count: String(count),
            remaining: String(remaining),
            total: this.formatMoney(this.discountedTotal()),
            subtotal: this.formatMoney(this.subtotal()),
            discount: String(this.bundleDiscountPercent() || this.subscriptionDiscountPercent()),
            max_discount: String(this.maxDiscount()),
            frequency: this.frequencyLabel(),
          },
          extra || {}
        );

        return String(template || '').replace(/\{(\w+)\}/g, (_, key) =>
          tokens[key] != null ? tokens[key] : `{${key}}`
        );
      }

      maxDiscount() {
        const tiers = this.config.tiers || [];
        return tiers.reduce((max, tier) => Math.max(max, Number(tier.percent) || 0), 0);
      }

      frequencyLabel() {
        const match = (this.config.frequencies || []).find((item) => item.value === this.frequency);
        return match ? match.label || match.value : this.frequency;
      }

      deliveryText() {
        if (this.purchaseType !== 'subscribe') {
          return this.config.messages.deliveryOnetime || '';
        }
        const match = (this.config.frequencies || []).find((item) => item.value === this.frequency);
        const template = (match && match.delivery) || this.config.messages.deliverySubscribe || '';
        return this.replaceTokens(template);
      }

      formatDeliveryHtml(text) {
        return this.escapeHtml(text || '').replace(
          /(\d+\s*(?:days?|weeks?|months?))/gi,
          '<strong>$1</strong>'
        );
      }

      ctaLabel() {
        const count = this.itemCount();
        const messages = this.config.messages;
        const firstThreshold = this.firstThresholdItems();
        const remaining = this.remainingToFinal();

        if (count < firstThreshold) {
          return this.replaceTokens(messages.ctaInactive || 'Add {remaining} items to get your discount', {
            remaining: String(Math.max(firstThreshold - count, 0)),
          });
        }

        if (remaining <= 0) {
          return this.replaceTokens(messages.atcWithPrice || 'Add To Cart · {total}');
        }

        if (remaining === 1) {
          return this.replaceTokens(messages.ctaAlmost || 'Add To Cart or {remaining} More Items', {
            remaining: String(remaining),
          });
        }

        return this.replaceTokens(messages.ctaMid || 'Add To Cart or {remaining} More Items', {
          remaining: String(remaining),
        });
      }

      desktopAtcLabel() {
        return this.ctaLabel();
      }

      syncDesktopAtcMinWidth() {
        const button = this.querySelector('[data-bundle-atc-desktop]');
        if (!button) return;

        const messages = this.config.messages || {};
        const wideTotal = this.formatMoney(Math.max(this.discountedTotal(), 99999));
        const samples = [
          this.replaceTokens(messages.ctaInactive || 'Add {remaining} items to get your discount', {
            remaining: String(this.firstThresholdItems()),
          }),
          this.replaceTokens(messages.ctaMid || 'Add To Cart or {remaining} More Items', { remaining: '2' }),
          this.replaceTokens(messages.ctaAlmost || 'Add To Cart or {remaining} More Items', { remaining: '1' }),
          this.replaceTokens(messages.atcWithPrice || 'Add To Cart · {total}', { total: wideTotal }),
        ];

        const probe = button.cloneNode(true);
        probe.removeAttribute('disabled');
        probe.setAttribute('aria-hidden', 'true');
        probe.style.cssText =
          'position:absolute;left:-9999px;top:0;visibility:hidden;display:inline-flex;width:auto;min-width:0;height:auto;pointer-events:none;white-space:nowrap;';
        document.body.appendChild(probe);

        const label = probe.querySelector('[data-bundle-atc-label]') || probe;
        let maxWidth = 0;
        samples.forEach((text) => {
          label.textContent = text;
          maxWidth = Math.max(maxWidth, Math.ceil(probe.getBoundingClientRect().width));
        });
        probe.remove();

        if (maxWidth > 0) {
          this.style.setProperty('--bb-atc-min-width', `${maxWidth}px`);
        }
      }

      setupDesktopStickyHeader() {
        if (!this.headerEl) return;

        this.desktopStickyMq = window.matchMedia('(min-width: 750px)');
        this.syncHeaderAnchorY();
        this.onDesktopStickyScroll = () => {
          if (this.stickyFrame) return;
          this.stickyFrame = window.requestAnimationFrame(() => {
            this.stickyFrame = null;
            this.updateDesktopStickyHeader();
          });
        };

        window.addEventListener('scroll', this.onDesktopStickyScroll, { passive: true });
        window.addEventListener('resize', this.onDesktopStickyScroll);
        this.updateDesktopStickyHeader();
      }

      syncHeaderAnchorY() {
        if (!this.headerEl || this.headerEl.classList.contains('is-stuck')) return;
        this.headerAnchorY = this.headerEl.getBoundingClientRect().top + window.scrollY;
      }

      updateDesktopStickyHeader() {
        if (!this.headerEl) return;

        const isDesktop = this.desktopStickyMq ? this.desktopStickyMq.matches : window.innerWidth >= 750;
        if (!isDesktop) {
          this.headerEl.classList.remove('is-stuck');
          if (this.headerSpacer) this.headerSpacer.style.height = '';
          return;
        }

        this.syncHeaderAnchorY();
        const shouldStick = window.scrollY > (this.headerAnchorY || 0);

        this.headerEl.classList.toggle('is-stuck', shouldStick);
        if (this.headerSpacer) {
          this.headerSpacer.style.height = shouldStick ? `${this.headerEl.offsetHeight}px` : '';
        }

        this.style.setProperty('--bb-tabs-sticky-top', `${Math.round(this.headerEl.offsetHeight)}px`);
      }

      setupMobileStickyTabs() {
        this.mobileStickyMq = window.matchMedia('(max-width: 749px)');
        this.onMobileStickyScroll = () => {
          if (this.tabsStickyFrame) return;
          this.tabsStickyFrame = window.requestAnimationFrame(() => {
            this.tabsStickyFrame = null;
            this.updateMobileStickyTabs();
          });
        };

        window.addEventListener('scroll', this.onMobileStickyScroll, { passive: true });
        window.addEventListener('resize', this.onMobileStickyScroll);
        this.updateMobileStickyTabs();
      }

      navOffset() {
        const header =
          document.querySelector('sticky-header') ||
          document.querySelector('.header-wrapper') ||
          document.querySelector('.section-header');

        if (header) {
          return Math.max(0, Math.round(header.getBoundingClientRect().bottom));
        }

        return parseFloat(getComputedStyle(document.documentElement).getPropertyValue('--header-height')) || 0;
      }

      updateMobileStickyTabs() {
        const isMobile = this.mobileStickyMq ? this.mobileStickyMq.matches : window.innerWidth < 750;
        if (!isMobile) return;

        this.style.setProperty('--bb-tabs-sticky-top', `${this.navOffset()}px`);
      }

      syncPurchaseType() {
        this.purchaseButtons.forEach((button) => {
          const isActive = button.dataset.bundlePurchase === this.purchaseType;
          button.classList.toggle('is-active', isActive);
          button.setAttribute('aria-pressed', isActive ? 'true' : 'false');
        });

        const showFrequency = this.purchaseType === 'subscribe' && this.config.frequencies.length > 0;
        this.frequencyWraps.forEach((wrap) => {
          wrap.hidden = !showFrequency;
        });
      }

      renderTabs() {
        this.tabButtons.forEach((button, index) => {
          const selected = index === this.activeTab;
          button.setAttribute('aria-selected', selected ? 'true' : 'false');
          button.tabIndex = selected ? 0 : -1;
        });

        this.panels.forEach((panel, index) => {
          panel.hidden = index !== this.activeTab;
        });

        this.renderPagination();
      }

      tabsAreSticky() {
        if (!this.tabsEl) return false;
        const stickyTop = parseFloat(getComputedStyle(this.tabsEl).top) || 0;
        return this.tabsEl.getBoundingClientRect().top <= stickyTop + 2;
      }

      scrollToProductGridIfSticky() {
        if (!this.tabsAreSticky()) return;

        const panel = this.panels[this.activeTab];
        const grid = (panel && panel.querySelector('.bundle-builder__grid')) || panel;
        if (!grid) return;

        const stickyTop = parseFloat(getComputedStyle(this.tabsEl).top) || 0;
        const tabsHeight = this.tabsEl.getBoundingClientRect().height;
        const top = grid.getBoundingClientRect().top + window.scrollY - stickyTop - tabsHeight;

        window.scrollTo({
          top: Math.max(0, Math.round(top)),
          behavior: 'smooth',
        });
      }

      visibleCardsForTab(tabIndex) {
        return this.cards.filter((card) => Number(card.dataset.tabIndex) === Number(tabIndex));
      }

      renderPagination() {
        const perPage = Number(this.config.productsPerPage) || 8;
        this.panels.forEach((panel, tabIndex) => {
          const cards = this.visibleCardsForTab(tabIndex);
          const page = this.pages[tabIndex] || 1;
          const totalPages = Math.max(1, Math.ceil(cards.length / perPage));
          const safePage = Math.min(page, totalPages);
          this.pages[tabIndex] = safePage;

          cards.forEach((card, index) => {
            const cardPage = Math.floor(index / perPage) + 1;
            card.hidden = cardPage !== safePage;
          });

          const pagination = panel.querySelector('[data-bundle-pagination]');
          if (!pagination) return;

          if (totalPages <= 1) {
            pagination.hidden = true;
            pagination.innerHTML = '';
            return;
          }

          pagination.hidden = false;
          pagination.innerHTML = this.paginationMarkup(safePage, totalPages);
          pagination.querySelectorAll('[data-page]').forEach((button) => {
            button.addEventListener('click', () => {
              this.pages[tabIndex] = Number(button.dataset.page);
              this.renderPagination();
            });
          });
        });
      }

      paginationMarkup(current, total) {
        const pages = [];
        if (total <= 5) {
          for (let i = 1; i <= total; i += 1) pages.push(i);
        } else if (current <= 3) {
          pages.push(1, 2, 3, 'gap', total);
        } else if (current >= total - 1) {
          pages.push(1, 2, 3, 'gap', total);
        } else {
          pages.push(1, 2, 3, 'gap', total);
        }

        const buttons = pages
          .map((page) => {
            if (page === 'gap') return '<span class="bundle-builder__page-gap" aria-hidden="true">…</span>';
            const currentAttr = page === current ? ' aria-current="page"' : '';
            return `<button type="button" class="bundle-builder__page" data-page="${page}"${currentAttr}>${page}</button>`;
          })
          .join('');

        const icon = this.config.nextPageIcon
          ? `<span class="bundle-builder__page-next-icon"><img src="${this.escapeAttr(
              this.config.nextPageIcon
            )}" alt="" width="20" height="6"></span>`
          : '';
        const next =
          current < total
            ? `<button type="button" class="bundle-builder__page-next" data-page="${current + 1}" aria-label="${this.escapeAttr(
                this.config.messages.nextPage || 'Next page'
              )}">${icon}</button>`
            : '';

        return `${buttons}${next}`;
      }

      updateAll() {
        this.updateCards();
        this.updateProgress();
        this.updateCopy();
        this.updateDrawerItems();
        this.updateSummary();
      }

      updateCards() {
        this.cards.forEach((card) => {
          const product = this.getCardProduct(card);
          if (!product) return;
          const item = this.items.get(String(product.id));
          card.classList.toggle('is-added', Boolean(item));
          const qty = card.querySelector('[data-bundle-qty-value]');
          if (qty && item) qty.textContent = String(item.quantity);

          if (item) {
            const select = card.querySelector('[data-bundle-variant]');
            if (select && String(select.value) !== String(item.variantId)) {
              select.value = String(item.variantId);
            }
          }

          const tabIndex = Number(card.dataset.tabIndex) || 0;
          const config = this.tabConfig(tabIndex);
          const addButton = card.querySelector('[data-bundle-add]');
          const plusButton = card.querySelector('[data-bundle-qty-plus]');
          const variant = this.getVariant(
            product,
            card.querySelector('[data-bundle-variant]')
              ? card.querySelector('[data-bundle-variant]').value
              : product.variants[0].id
          );
          const maxInBundle = Number(this.config.maxProductsInBundle) || 0;
          const bundleFull = maxInBundle > 0 && this.itemCount() >= maxInBundle;
          const stepFull =
            config.customQuantity &&
            Number(config.maxPerStep) > 0 &&
            this.tabItemCount(tabIndex) >= Number(config.maxPerStep);
          const productFull =
            config.customQuantity &&
            Number(config.maxPerProduct) > 0 &&
            (item ? item.quantity : 0) >= Number(config.maxPerProduct);

          if (addButton) addButton.disabled = !variant || !variant.available || stepFull || bundleFull;
          if (plusButton) plusButton.disabled = productFull || stepFull || bundleFull;
          if (variant) this.updateCardPrice(card, variant);
        });
      }

      activeDiscountPercent() {
        return this.bundleDiscountPercent();
      }

      discountedAmount(cents) {
        const percent = this.activeDiscountPercent();
        const amount = Number(cents) || 0;
        if (percent <= 0) return amount;
        return Math.round(amount * (1 - percent / 100));
      }

      updateCardPrice(card, variant) {
        const price = card.querySelector('[data-bundle-price]');
        const compare = card.querySelector('[data-bundle-compare]');
        const wrap = card.querySelector('.bundle-builder-card__price');
        if (!price) return;

        const original = Number(variant.price) || 0;
        const compareAt = Number(variant.compare_at_price) || 0;
        const sale = this.discountedAmount(original);
        const hasTierDiscount = sale < original;
        const hasCompare = compareAt > original;

        price.textContent = this.formatMoney(sale);
        if (wrap) wrap.classList.toggle('has-discount', hasTierDiscount);

        if (!compare) return;
        if (hasTierDiscount) {
          compare.hidden = false;
          compare.textContent = this.formatMoney(original);
        } else if (hasCompare) {
          compare.hidden = false;
          compare.textContent = this.formatMoney(compareAt);
        } else {
          compare.hidden = true;
        }
      }

      updateProgress() {
        const count = this.itemCount();
        const maxItems = this.maxTierItems();
        const percent = maxItems > 0 ? Math.min(100, (count / maxItems) * 100) : 0;

        this.style.setProperty('--bb-progress-percent', `${percent}%`);
        this.progressFills.forEach((fill) => {
          fill.style.width = '';
        });

        this.progressNodes.forEach((node) => {
          const threshold = Number(node.dataset.bundleNode) || 0;
          node.classList.toggle('is-achieved', count >= threshold);
        });
      }

      updateCopy() {
        const countText = this.replaceTokens(this.config.messages.itemsAdded || '{count} items added');
        this.countEls.forEach((el) => {
          el.textContent = countText;
        });

        this.headingEls.forEach((el) => {
          el.textContent = this.replaceTokens(this.config.messages.progressHeading || 'Save up to {max_discount}% with your bundle');
        });

        this.deliveryTexts.forEach((el) => {
          const text = this.deliveryText();
          el.innerHTML = this.formatDeliveryHtml(text);
          el.hidden = text.trim() === '';
        });

        const min = Number(this.config.minItemsToCheckout) || 0;
        const unmetTab = this.unmetStepMinimum();
        const canCheckout = this.itemCount() >= min && this.itemCount() > 0 && !unmetTab;

        this.atcButtons.forEach((button) => {
          const label = button.querySelector('[data-bundle-atc-label]') || button;
          label.textContent = this.desktopAtcLabel();
          button.disabled = !canCheckout;
        });

        if (unmetTab) {
          const tabIndex = (this.config.tabs || []).indexOf(unmetTab);
          this.setTabLimitMessage(
            tabIndex,
            this.quantityLimitMessage('minPerStep', {
              min: String(unmetTab.minPerStep),
              tab: unmetTab.title || '',
            })
          );
        }

        if (this.drawerTitle) {
          this.drawerTitle.textContent = this.replaceTokens(this.config.messages.drawerTitle || 'Your Box ({count} items)');
        }
      }

      updateDrawerItems() {
        if (!this.drawerItems) return;

        if (this.items.size === 0) {
          this.drawerItems.innerHTML = '';
          this.drawerItems.hidden = true;
          return;
        }

        const removeLabel = this.config.messages.removeItem || 'Remove';
        const parts = [];

        this.items.forEach((item) => {
          const card =
            this.cards.find(
              (node) =>
                Number(node.dataset.productId) === Number(item.productId) &&
                Number(node.dataset.tabIndex) === Number(item.tabIndex)
            ) || this.cards.find((node) => Number(node.dataset.productId) === Number(item.productId));
          const product = card ? this.getCardProduct(card) : null;
          const variant = product ? this.getVariant(product, item.variantId) : null;
          const image =
            (variant && variant.featured_image) ||
            (product && product.featured_image) ||
            (card && card.querySelector('[data-bundle-image]') && card.querySelector('[data-bundle-image]').getAttribute('src')) ||
            '';
          const title = item.title || (product && product.title) || '';
          const label = `${removeLabel} ${title}`.trim();

          parts.push(
            `<article class="bundle-builder__drawer-item">
              <div class="bundle-builder__drawer-item-media">
                ${
                  image
                    ? `<img class="bundle-builder__drawer-item-image" src="${this.escapeAttr(image)}" alt="${this.escapeAttr(title)}" width="120" height="120">`
                    : `<span class="bundle-builder__drawer-item-image" aria-hidden="true"></span>`
                }
                <button type="button" class="bundle-builder__drawer-item-remove" data-bundle-remove="${this.escapeAttr(
                  String(item.productId)
                )}" aria-label="${this.escapeAttr(label)}">
                  <svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 12 12" width="10" height="10" aria-hidden="true" focusable="false">
                    <path fill="currentColor" d="M1.2.5 6 5.3 10.8.5l.7.7L6.7 6l4.8 4.8-.7.7L6 6.7 1.2 11.5l-.7-.7L5.3 6 .5 1.2z"/>
                  </svg>
                </button>
                ${
                  item.quantity > 1
                    ? `<span class="bundle-builder__drawer-item-qty">${this.escapeHtml(String(item.quantity))}</span>`
                    : ''
                }
              </div>
              <p class="bundle-builder__drawer-item-title">${this.escapeHtml(title)}</p>
            </article>`
          );
        });

        this.drawerItems.hidden = false;
        this.drawerItems.innerHTML = parts.join('');
      }

      updateSummary() {
        const subtotal = this.subtotal();
        const bundlePercent = this.bundleDiscountPercent();
        const subPercent = this.subscriptionDiscountPercent();
        const bundleAmount = Math.round(subtotal * (bundlePercent / 100));
        const subAmount = Math.round(subtotal * (subPercent / 100));
        const total = this.discountedTotal();

        if (this.stickyTotal) this.stickyTotal.textContent = this.formatMoney(total);
        if (this.stickyCompare) {
          if (total < subtotal) {
            this.stickyCompare.hidden = false;
            this.stickyCompare.textContent = this.formatMoney(subtotal);
          } else {
            this.stickyCompare.hidden = true;
          }
        }

        if (!this.summaryRoot) return;

        const rows = [
          {
            label: this.config.messages.subtotalLabel || 'Subtotal',
            value: this.formatMoney(subtotal),
            modifier: '',
          },
        ];

        if (bundlePercent > 0) {
          rows.push({
            label: this.replaceTokens(this.config.messages.bundleDiscountLabel || 'Bundle discount ({discount}%)'),
            value: `-${this.formatMoney(bundleAmount)}`,
            modifier: 'bundle-builder__summary-row--discount',
          });
        }

        if (subPercent > 0) {
          rows.push({
            label: this.replaceTokens(this.config.messages.subscriptionDiscountLabel || 'Subscription ({discount}%)'),
            value: `-${this.formatMoney(subAmount)}`,
            modifier: 'bundle-builder__summary-row--discount',
          });
        }

        rows.push({
          label: this.config.messages.totalLabel || 'Total',
          value: this.formatMoney(total),
          modifier: 'bundle-builder__summary-row--total',
        });

        this.summaryRoot.innerHTML = rows
          .map(
            (row) =>
              `<div class="bundle-builder__summary-row ${row.modifier}"><span>${this.escapeHtml(
                row.label
              )}</span><span>${this.escapeHtml(row.value)}</span></div>`
          )
          .join('');
      }

      sellingPlanForVariant(product, variantId) {
        if (this.purchaseType !== 'subscribe') return null;
        const variant = this.getVariant(product, variantId);
        if (!variant || !variant.selling_plans || !variant.selling_plans.length) return null;

        const selectedKey = this.frequencyIntervalKey(this.frequency);
        if (!selectedKey) return null;

        const plans = variant.selling_plans.filter((plan) => !this.isPromotionalPlan(plan.name));
        const pool = plans.length ? plans : variant.selling_plans;
        return (
          pool.find((plan) => this.frequencyIntervalKey(plan.name || plan.option) === selectedKey) || null
        );
      }

      createBundleInstanceId() {
        if (window.crypto && typeof window.crypto.randomUUID === 'function') {
          return window.crypto.randomUUID();
        }

        return `${Date.now().toString(36)}-${Math.random().toString(36).slice(2, 10)}`;
      }

      async addBundleToCart(button) {
        if (this.itemCount() <= 0) return;

        const items = [];
        const bundleInstanceId = this.purchaseType === 'onetime' ? this.createBundleInstanceId() : '';
        const bundleSize = this.itemCount();

        this.items.forEach((item) => {
          const card = this.cards.find((node) => Number(node.dataset.productId) === Number(item.productId));
          const product = card ? this.getCardProduct(card) : null;
          const payload = {
            id: Number(item.variantId),
            quantity: Number(item.quantity),
          };

          if (this.purchaseType === 'onetime') {
            payload.properties = Object.assign({}, this.config.properties, {
              _bundle_purchase_type: 'onetime',
              _bundle_instance: bundleInstanceId,
              _bundle_size: String(bundleSize),
            });
          }

          const plan = product ? this.sellingPlanForVariant(product, item.variantId) : null;
          if (plan && plan.id) payload.selling_plan = Number(plan.id);

          items.push(payload);
        });

        const cartDrawer = document.querySelector('cart-drawer');
        const cartNotification = document.querySelector('cart-notification');
        const cart = cartDrawer || cartNotification;
        const body = { items };

        if (cartDrawer && typeof cartDrawer.getSectionsToRender === 'function') {
          body.sections = cartDrawer
            .getSectionsToRender()
            .map((section) => section.id)
            .join(',');
          body.sections_url = window.location.pathname;
        } else {
          body.sections = 'cart-icon-bubble';
          body.sections_url = window.location.pathname;
        }

        this.setLoading(button, true);
        this.setError('');

        try {
          const config = fetchConfig('javascript');
          config.body = JSON.stringify(body);
          const response = await fetch(`${window.routes.cart_add_url}.js`.replace(/\.js\.js$/, '.js'), config);
          const data = await response.json();

          if (data.status) {
            this.setError(data.description || data.message || this.config.messages.cartError || 'Unable to add bundle to cart.');
            return;
          }

          this.items.clear();
          this.closeDrawer();
          this.updateAll();

          try {
            if (data.sections && data.sections['cart-icon-bubble']) {
              const bubble = document.getElementById('cart-icon-bubble');
              if (bubble) {
                const parsed = new DOMParser().parseFromString(data.sections['cart-icon-bubble'], 'text/html');
                const next = parsed.querySelector('.shopify-section') || parsed.body;
                if (next) bubble.innerHTML = next.innerHTML;
              }
            }

            if (window.Rebuy && window.Rebuy.SmartCart) {
              if (typeof window.Rebuy.SmartCart.setCartAsync === 'function') {
                await window.Rebuy.SmartCart.setCartAsync();
              }
              window.Rebuy.SmartCart.show();
            } else if (cartDrawer && typeof cartDrawer.renderContents === 'function' && data.sections) {
              cartDrawer.classList.remove('is-empty');
              cartDrawer.renderContents(data);
            } else if (
              cartNotification &&
              data.key &&
              typeof cartNotification.renderContents === 'function' &&
              data.sections
            ) {
              cartNotification.renderContents(data);
            }

            if (typeof publish === 'function' && window.PUB_SUB_EVENTS) {
              publish(PUB_SUB_EVENTS.cartUpdate, {
                source: 'bundle-builder',
                cartData: data,
              });
            }
          } catch (refreshError) {
            console.error(refreshError);
          }
        } catch (error) {
          this.setError(this.config.messages.cartError || 'Unable to add bundle to cart.');
        } finally {
          this.setLoading(button, false);
        }
      }

      setLoading(button, isLoading) {
        this.atcButtons.forEach((el) => {
          el.classList.toggle('is-loading', isLoading);
          if (isLoading) el.setAttribute('aria-disabled', 'true');
          else el.removeAttribute('aria-disabled');
        });
        if (button) button.classList.toggle('is-loading', isLoading);
      }

      setError(message) {
        this.errorEls.forEach((el) => {
          el.textContent = message || '';
        });
      }

      setFloatingWidgetsHidden(hidden) {
        const nodes = document.querySelectorAll(
          '#reamazejs-container, #reamaze-widget, iframe[id*="reamaze"], iframe[src*="reamaze"], access-widget-ui, #ps__widget, #ps__widget_container, iframe#ps__widget'
        );
        nodes.forEach((el) => {
          if (hidden) {
            el.style.setProperty('display', 'none', 'important');
            el.style.setProperty('visibility', 'hidden', 'important');
            el.setAttribute('aria-hidden', 'true');
          } else {
            el.style.removeProperty('display');
            el.style.removeProperty('visibility');
            el.removeAttribute('aria-hidden');
          }
        });

        document.querySelectorAll('access-widget-ui').forEach((host) => {
          const trigger = host.shadowRoot && host.shadowRoot.querySelector('[part="acsb-trigger"]');
          if (!trigger) return;
          if (hidden) {
            trigger.style.setProperty('display', 'none', 'important');
            trigger.style.setProperty('visibility', 'hidden', 'important');
          } else {
            trigger.style.removeProperty('display');
            trigger.style.removeProperty('visibility');
          }
        });
      }

      openDrawer(trigger) {
        if (!this.drawer) return;
        this.lastFocus = trigger || document.activeElement;
        this.classList.add('is-drawer-open');
        document.body.classList.add('bundle-builder-drawer-open');
        this.setFloatingWidgetsHidden(true);
        this.drawer.setAttribute('aria-hidden', 'false');
        if (this.drawerOverlay) this.drawerOverlay.hidden = false;
        const closeButton = this.drawer.querySelector('[data-bundle-close]');
        if (closeButton) closeButton.focus();
      }

      closeDrawer() {
        this.classList.remove('is-drawer-open');
        document.body.classList.remove('bundle-builder-drawer-open');
        this.setFloatingWidgetsHidden(false);
        if (this.drawer) this.drawer.setAttribute('aria-hidden', 'true');
        if (this.drawerOverlay) this.drawerOverlay.hidden = true;
        if (this.lastFocus && typeof this.lastFocus.focus === 'function') {
          this.lastFocus.focus();
        }
      }

      formatMoney(cents) {
        const value = (Number(cents || 0) / 100).toFixed(2);
        const format = this.config.moneyFormat || '${{amount}}';
        const withComma = value.replace('.', ',');
        return format
          .replace(/\{\{\s*amount_with_comma_separator\s*\}\}/g, withComma)
          .replace(/\{\{\s*amount_no_decimals\s*\}\}/g, String(Math.round(Number(cents || 0) / 100)))
          .replace(/\{\{\s*amount_with_space_separator\s*\}\}/g, value.replace('.', ' '))
          .replace(/\{\{\s*amount\s*\}\}/g, value);
      }

      escapeHtml(value) {
        return String(value)
          .replace(/&/g, '&amp;')
          .replace(/</g, '&lt;')
          .replace(/>/g, '&gt;')
          .replace(/"/g, '&quot;')
          .replace(/'/g, '&#39;');
      }

      escapeAttr(value) {
        return this.escapeHtml(value);
      }
    }
  );
}
